import owner from './owner';
import distributor from './distributor';
import reseller from './reseller';
import customer from './customer';

// Role mapping (should match the role_id from server)
export const ROLES = {
  OWNER: 1,
  DISTRIBUTOR: 2,
  RESELLER: 3,
  CUSTOMER: 4
} as const;

// Role name mapping
export const ROLE_NAMES = {
  [ROLES.OWNER]: 'owner',
  [ROLES.DISTRIBUTOR]: 'distributor',
  [ROLES.RESELLER]: 'reseller',
  [ROLES.CUSTOMER]: 'customer'
} as const;

export interface MenuItem {
  id: string;
  title: string;
  type: 'group' | 'item' | 'collapse';
  url?: string;
  icon?: any;
  breadcrumbs?: boolean;
  children?: MenuItem[];
}

export interface MenuItems {
  items: MenuItem[];
}

/**
 * Get menu items based on user role ID
 * @param roleId - The role ID from the authenticated user (1: Owner, 2: Distributor, 3: Reseller, 4: Customer)
 * @returns MenuItems object for the specific role
 */
export const getMenuByRoleId = (roleId: number | undefined): MenuItems => {
  if (!roleId) {
    // Return minimal menu for unauthenticated users
    return { items: [] };
  }

  switch (roleId) {
    case ROLES.OWNER:
      return { items: [owner as MenuItem] };
    case ROLES.DISTRIBUTOR:
      return { items: [distributor as MenuItem] };
    case ROLES.RESELLER:
      return { items: [reseller as MenuItem] };
    case ROLES.CUSTOMER:
      return { items: [customer as MenuItem] };
    default:
      console.warn(`Unknown role ID: ${roleId}. Returning empty menu.`);
      return { items: [] };
  }
};

/**
 * Get menu items based on user role name
 * @param roleName - The role name from the authenticated user (owner, distributor, reseller, customer)
 * @returns MenuItems object for the specific role
 */
export const getMenuByRoleName = (roleName: string | undefined): MenuItems => {
  if (!roleName) {
    return { items: [] };
  }

  const normalizedRole = roleName.toLowerCase();

  switch (normalizedRole) {
    case 'owner':
      return { items: [owner as MenuItem] };
    case 'distributor':
      return { items: [distributor as MenuItem] };
    case 'reseller':
      return { items: [reseller as MenuItem] };
    case 'customer':
      return { items: [customer as MenuItem] };
    default:
      console.warn(`Unknown role name: ${roleName}. Returning empty menu.`);
      return { items: [] };
  }
};

/**
 * Get all available menu items (useful for testing or admin views)
 * @returns Object with all menu configurations
 */
export const getAllMenus = () => {
  return {
    owner,
    distributor,
    reseller,
    customer
  };
};

/**
 * Check if a role has access to a specific menu item
 * @param roleId - The user's role ID
 * @param menuItemId - The ID of the menu item to check
 * @returns boolean indicating if the role has access
 */
export const hasMenuAccess = (roleId: number | undefined, menuItemId: string): boolean => {
  if (!roleId) return false;

  const menu = getMenuByRoleId(roleId);
  
  // Recursively search for menu item
  const searchMenu = (items: MenuItem[]): boolean => {
    for (const item of items) {
      if (item.id === menuItemId) return true;
      if (item.children && searchMenu(item.children)) return true;
    }
    return false;
  };

  return searchMenu(menu.items);
};

export default getMenuByRoleId;

